import * as THREE from 'three';

/**
 * Bevelled / chamfered primitive library.
 *
 * Rule for this whole directory: **nothing returns an unbevelled 90 degree edge.**
 * Every silhouette edge on the viewmodel has to catch a specular highlight line,
 * because that is the single loudest amateur tell at 30 cm from the camera.
 *
 * Units are metres. Bevels are typically 0.0004 - 0.0010 m (0.4 - 1.0 mm), which is
 * the real chamfer size a machinist leaves on an aluminium receiver.
 */

const EPS = 1e-6;

/* ------------------------------------------------------------------ *
 * 2D shape helpers
 * ------------------------------------------------------------------ */

/** Rounded rectangle centred on the origin. */
export function roundedRectShape(w, h, r, seg = 4) {
  const s = new THREE.Shape();
  const hw = w * 0.5, hh = h * 0.5;
  r = Math.min(r, hw - EPS, hh - EPS);
  s.moveTo(-hw + r, -hh);
  s.lineTo(hw - r, -hh);
  s.quadraticCurveTo(hw, -hh, hw, -hh + r);
  s.lineTo(hw, hh - r);
  s.quadraticCurveTo(hw, hh, hw - r, hh);
  s.lineTo(-hw + r, hh);
  s.quadraticCurveTo(-hw, hh, -hw, hh - r);
  s.lineTo(-hw, -hh + r);
  s.quadraticCurveTo(-hw, -hh, -hw + r, -hh);
  s.closePath();
  s.curveSegments = seg;
  return s;
}

/**
 * Polygon with a fillet at every corner. `pts` is [[x,y], ...] in CCW order.
 * `radius` may be a number or a per-corner array.
 */
export function filletShape(pts, radius = 0.001, seg = 3) {
  const n = pts.length;
  const R = (i) => (Array.isArray(radius) ? radius[i] ?? 0 : radius);
  const s = new THREE.Shape();
  s.curveSegments = seg;

  const corner = (i) => {
    const p = pts[i];
    const prev = pts[(i - 1 + n) % n];
    const next = pts[(i + 1) % n];
    const d0x = prev[0] - p[0], d0y = prev[1] - p[1];
    const d1x = next[0] - p[0], d1y = next[1] - p[1];
    const l0 = Math.hypot(d0x, d0y) || 1;
    const l1 = Math.hypot(d1x, d1y) || 1;
    let r = Math.min(R(i), l0 * 0.49, l1 * 0.49);
    if (r < EPS) return { a: [p[0], p[1]], b: [p[0], p[1]], c: p, r: 0 };
    return {
      a: [p[0] + (d0x / l0) * r, p[1] + (d0y / l0) * r], // arriving point
      b: [p[0] + (d1x / l1) * r, p[1] + (d1y / l1) * r], // departing point
      c: p,
      r,
    };
  };

  const cs = [];
  for (let i = 0; i < n; i++) cs.push(corner(i));

  s.moveTo(cs[0].b[0], cs[0].b[1]);
  for (let i = 1; i <= n; i++) {
    const c = cs[i % n];
    s.lineTo(c.a[0], c.a[1]);
    if (c.r > EPS) s.quadraticCurveTo(c.c[0], c.c[1], c.b[0], c.b[1]);
  }
  s.closePath();
  return s;
}

/* ------------------------------------------------------------------ *
 * Core extrusion
 * ------------------------------------------------------------------ */

/**
 * Extrude a 2D shape along +Z and centre the result, with a real chamfer on the
 * front and back faces. This is the workhorse: every flat-sided weapon part is one
 * of these.
 */
export function extrudeShape(shape, depth, opts = {}) {
  const bevel = opts.bevel ?? 0.0006;
  const bevelSegments = opts.bevelSegments ?? 2;
  const b = Math.min(bevel, depth * 0.45);
  const g = new THREE.ExtrudeGeometry(shape, {
    depth: Math.max(EPS, depth - 2 * b),
    bevelEnabled: b > EPS,
    bevelThickness: b,
    bevelSize: b,
    bevelOffset: 0,
    bevelSegments: b > EPS ? bevelSegments : 0,
    curveSegments: opts.curveSegments ?? shape.curveSegments ?? 4,
    steps: opts.steps ?? 1,
  });
  g.translate(0, 0, -(depth * 0.5 - b));
  g.computeVertexNormals();
  return g;
}

/** Box with all twelve edges chamfered. Centred on the origin. */
export function bevelBox(w, h, d, bevel = 0.0006, opts = {}) {
  const corner = opts.corner ?? bevel * 1.7;
  const shape = roundedRectShape(w, h, corner, opts.curveSegments ?? 4);
  return extrudeShape(shape, d, { ...opts, bevel });
}

/** Box that tapers along its depth (front face smaller). Chamfered. */
export function taperBox(w0, h0, w1, h1, d, bevel = 0.0006, seg = 6) {
  // Built as a lofted prism with rounded corners approximated per ring.
  const rings = [];
  const N = 4 * seg;
  const cornerR = bevel * 1.7;
  for (let s = 0; s <= 1; s++) {
    const w = s === 0 ? w0 : w1;
    const h = s === 0 ? h0 : h1;
    rings.push(roundedRectPoints(w, h, Math.min(cornerR, w * 0.4, h * 0.4), N));
  }
  return loft(rings, [-d * 0.5, d * 0.5], { capStart: true, capEnd: true, bevel });
}

function roundedRectPoints(w, h, r, n) {
  const hw = w * 0.5 - r, hh = h * 0.5 - r;
  const pts = [];
  for (let i = 0; i < n; i++) {
    const t = (i / n) * Math.PI * 2;
    // squircle-ish param: pick the quadrant centre then offset by r
    const cx = Math.cos(t) >= 0 ? hw : -hw;
    const cy = Math.sin(t) >= 0 ? hh : -hh;
    pts.push([cx + Math.cos(t) * r, cy + Math.sin(t) * r]);
  }
  return pts;
}

/**
 * Loft a set of equal-length 2D rings placed at given Z positions.
 * Indexed, so `computeVertexNormals` smooths around each ring and leaves the caps
 * hard — which is what a moulded polymer part actually looks like.
 */
export function loft(rings, zs, opts = {}) {
  const n = rings[0].length;
  const R = rings.length;
  const pos = [];
  const idx = [];
  for (let s = 0; s < R; s++) {
    for (let i = 0; i < n; i++) pos.push(rings[s][i][0], rings[s][i][1], zs[s]);
  }
  for (let s = 0; s < R - 1; s++) {
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      const a = s * n + i, b = s * n + j, c = (s + 1) * n + j, d = (s + 1) * n + i;
      idx.push(a, b, c, a, c, d);
    }
  }
  const capRing = (ringIndex, z, flip) => {
    const ring = rings[ringIndex];
    let cx = 0, cy = 0;
    for (const p of ring) { cx += p[0]; cy += p[1]; }
    cx /= n; cy /= n;
    const base = pos.length / 3;
    pos.push(cx, cy, z);
    for (const p of ring) pos.push(p[0], p[1], z);
    for (let i = 0; i < n; i++) {
      const a = base, b = base + 1 + i, c = base + 1 + ((i + 1) % n);
      if (flip) idx.push(a, c, b); else idx.push(a, b, c);
    }
  };
  if (opts.capStart !== false) capRing(0, zs[0], true);
  if (opts.capEnd !== false) capRing(R - 1, zs[R - 1], false);

  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

/** Rounded-rectangle ring, exported so weapon parts can loft their own profiles. */
export function ringRoundedRect(w, h, r, n) {
  return roundedRectPoints(w, h, Math.min(r, w * 0.49, h * 0.49), n);
}

/**
 * Loft a smoothly varying rounded-box section along +Z.
 * `fn(t)` returns `{ w, h, r }` for t in [0, 1].
 */
export function loftSection(fn, length, steps = 10, ringSegs = 28) {
  const rings = [], zs = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const p = fn(t);
    rings.push(ringRoundedRect(p.w, p.h, p.r, ringSegs));
    zs.push(t * length);
  }
  return loft(rings, zs);
}

/* ------------------------------------------------------------------ *
 * Rotational parts
 * ------------------------------------------------------------------ */

/** Lathe a profile [[x,y], ...] around the Y axis. x must be >= 0. */
export function lathe(profile, segments = 24, phiStart = 0, phiLength = Math.PI * 2) {
  const pts = profile.map((p) => new THREE.Vector2(Math.max(p[0], 1e-5), p[1]));
  const g = new THREE.LatheGeometry(pts, segments, phiStart, phiLength);
  return g;
}

/** Cylinder along Y with a 45 degree chamfer at both ends. */
export function chamferCyl(r, h, chamfer = 0.0005, segments = 20, opts = {}) {
  const c = Math.min(chamfer, r * 0.45, h * 0.45);
  const rInner = opts.hole ?? 0;
  const prof = [];
  if (rInner > 0) {
    prof.push([rInner, -h / 2], [rInner, h / 2], [r - c, h / 2], [r, h / 2 - c],
      [r, -h / 2 + c], [r - c, -h / 2], [rInner, -h / 2]);
    return lathe(prof, segments);
  }
  prof.push([0.00002, -h / 2], [r - c, -h / 2], [r, -h / 2 + c], [r, h / 2 - c],
    [r - c, h / 2], [0.00002, h / 2]);
  return lathe(prof, segments);
}

/** Tapered cylinder (cone frustum) with chamfered ends, along Y. */
export function chamferCone(r0, r1, h, chamfer = 0.0005, segments = 20) {
  const c = chamfer;
  return lathe([
    [0.00002, -h / 2], [r0 - c, -h / 2], [r0, -h / 2 + c],
    [r1, h / 2 - c], [r1 - c, h / 2], [0.00002, h / 2],
  ], segments);
}

/** Hollow tube along Y with chamfered lips (barrel bores, buffer tubes, optic bodies). */
export function tubeY(rOuter, rInner, h, chamfer = 0.0005, segments = 24) {
  const c = chamfer;
  return lathe([
    [rInner, -h / 2], [rOuter - c, -h / 2], [rOuter, -h / 2 + c],
    [rOuter, h / 2 - c], [rOuter - c, h / 2], [rInner + c, h / 2],
    [rInner, h / 2 - c], [rInner, -h / 2],
  ], segments);
}

/** Hex bar along Y (nuts, castle-ish nuts, flash-hider bodies), edges chamfered. */
export function hexBar(acrossFlats, h, chamfer = 0.0004) {
  const r = acrossFlats / Math.cos(Math.PI / 6) / 2;
  const pts = [];
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + Math.PI / 6;
    pts.push([Math.cos(a) * r, Math.sin(a) * r]);
  }
  const g = extrudeShape(filletShape(pts, chamfer * 1.5, 2), h, { bevel: chamfer });
  g.rotateX(-Math.PI / 2); // extruded along Z -> align to Y
  return g;
}

/* ------------------------------------------------------------------ *
 * Fasteners and micro-detail
 * ------------------------------------------------------------------ */

/**
 * Hex-socket cap screw, head centred at the origin, shank going -Y.
 * `r` is head radius. Reads correctly from 20 cm.
 */
export function capScrew(r = 0.0022, headH = 0.0016, shank = 0.004) {
  const sock = r * 0.55;
  const prof = [
    [0.00002, -headH / 2 - shank],
    [r * 0.42, -headH / 2 - shank],
    [r * 0.42, -headH / 2],
    [r - 0.0002, -headH / 2],
    [r, -headH / 2 + 0.0002],
    [r, headH / 2 - 0.00025],
    [r - 0.00025, headH / 2],
    [sock + 0.0002, headH / 2],
    [sock, headH / 2 - 0.0002],
    [sock, headH / 2 - headH * 0.55],
    [0.00002, headH / 2 - headH * 0.62],
  ];
  return lathe(prof, 12);
}

/** Flat slotted / pan-head pin used for takedown pins and roll pins. */
export function rollPin(r = 0.0018, len = 0.006) {
  return lathe([
    [0.00002, -len / 2], [r - 0.0003, -len / 2], [r, -len / 2 + 0.0003],
    [r, len / 2 - 0.0003], [r - 0.0003, len / 2], [r * 0.45, len / 2],
    [r * 0.45, len / 2 - 0.0006], [0.00002, len / 2 - 0.0008],
  ], 10);
}

/* ------------------------------------------------------------------ *
 * Surface treatments
 * ------------------------------------------------------------------ */

/**
 * Real geometric stippling: a field of tiny truncated pyramids on the XY plane
 * (facing +Z). This is what makes a polymer grip read as grippy under a highlight
 * instead of looking like a smooth plastic slab.
 */
export function stippleField(w, h, rng, opts = {}) {
  const pitch = opts.pitch ?? 0.0016;
  const height = opts.height ?? 0.00042;
  const jitter = opts.jitter ?? 0.32;
  const mask = opts.mask ?? (() => true);
  const cols = Math.max(1, Math.floor(w / pitch));
  const rows = Math.max(1, Math.floor(h / pitch));
  const pos = [];
  const half = pitch * 0.36;
  const tip = half * 0.30;

  for (let iy = 0; iy < rows; iy++) {
    for (let ix = 0; ix < cols; ix++) {
      const stagger = (iy & 1) * 0.5;
      let x = (ix + 0.5 + stagger) * pitch - w * 0.5;
      let y = (iy + 0.5) * pitch - h * 0.5;
      if (x > w * 0.5) continue;
      const u = (x + w * 0.5) / w, v = (y + h * 0.5) / h;
      if (!mask(u, v)) continue;
      x += (rng() - 0.5) * pitch * jitter;
      y += (rng() - 0.5) * pitch * jitter;
      const s = 0.78 + rng() * 0.44;
      const b = half * s, t = tip * s, hz = height * (0.7 + rng() * 0.6);
      // 4 side quads + top quad
      const base = [[-b, -b], [b, -b], [b, b], [-b, b]];
      const top = [[-t, -t], [t, -t], [t, t], [-t, t]];
      for (let k = 0; k < 4; k++) {
        const k2 = (k + 1) % 4;
        const a = [x + base[k][0], y + base[k][1], 0];
        const bb = [x + base[k2][0], y + base[k2][1], 0];
        const c = [x + top[k2][0], y + top[k2][1], hz];
        const d = [x + top[k][0], y + top[k][1], hz];
        pos.push(...a, ...bb, ...c, ...a, ...c, ...d);
      }
      const t0 = [x + top[0][0], y + top[0][1], hz];
      const t1 = [x + top[1][0], y + top[1][1], hz];
      const t2 = [x + top[2][0], y + top[2][1], hz];
      const t3 = [x + top[3][0], y + top[3][1], hz];
      pos.push(...t0, ...t1, ...t2, ...t0, ...t2, ...t3);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.computeVertexNormals();
  return g;
}

/**
 * MIL-STD-1913 Picatinny rail running along Z, sitting on y = 0, centred on x = 0.
 * Built as a base bar plus discrete cleats, which is literally how the recoil
 * grooves exist on the real part. Cleat pitch 10 mm.
 */
export function picatinnyRail(length, opts = {}) {
  const scale = opts.scale ?? 1;
  const W = 0.0212 * scale;      // widest, at the flare
  const TOPW = 0.0156 * scale;   // top flat
  const BASEW = 0.0182 * scale;
  const H = 0.0091 * scale;      // total height above the mounting surface
  const GROOVE = 0.0032 * scale; // groove floor height
  const bev = 0.00035;

  const cleatProfile = filletShape([
    [-BASEW / 2, 0], [BASEW / 2, 0],
    [BASEW / 2, GROOVE * 0.55], [W / 2, GROOVE * 1.25],
    [W / 2, H - (W - TOPW) / 2], [TOPW / 2, H],
    [-TOPW / 2, H], [-W / 2, H - (W - TOPW) / 2],
    [-W / 2, GROOVE * 1.25], [-BASEW / 2, GROOVE * 0.55],
  ], 0.0004, 2);

  const baseProfile = filletShape([
    [-BASEW / 2, 0], [BASEW / 2, 0], [BASEW / 2, GROOVE], [-BASEW / 2, GROOVE],
  ], 0.0003, 2);

  const parts = [];
  const base = extrudeShape(baseProfile, length, { bevel: bev });
  parts.push(base);

  const pitch = 0.0100 * scale;
  const cleatW = 0.0046 * scale;
  const n = Math.max(1, Math.floor(length / pitch));
  const startZ = -length / 2 + (length - (n - 1) * pitch) / 2;
  for (let i = 0; i < n; i++) {
    const c = extrudeShape(cleatProfile, cleatW, { bevel: bev });
    c.translate(0, 0, startZ + i * pitch);
    parts.push(c);
  }
  return { parts, height: H, top: H };
}

/**
 * A run of M-LOK slots along a flat panel lying in the XZ plane (panel normal +Y).
 * Returns the panel geometry *with* slot openings, built from longitudinal rails
 * and crossbars — no CSG needed and the slot edges get real chamfers.
 */
export function mlokPanel(length, width, thickness, opts = {}) {
  const slotL = opts.slotLength ?? 0.0320;
  const slotW = opts.slotWidth ?? 0.0070;
  const pitch = opts.pitch ?? 0.0400;
  const count = opts.count ?? Math.max(0, Math.floor((length - 0.012) / pitch));
  const bev = opts.bevel ?? 0.0005;
  const parts = [];

  // bevelBox(w, h, d) already lays out X = width, Y = thickness, Z = length.
  const sideW = (width - slotW) / 2;
  for (const sx of [-1, 1]) {
    const g = bevelBox(sideW, thickness, length, bev);
    g.translate(sx * (slotW / 2 + sideW / 2), 0, 0);
    parts.push(g);
  }
  if (count > 0) {
    const span = (count - 1) * pitch;
    const startZ = -span / 2;
    const barLen = pitch - slotL;
    for (let i = 0; i <= count; i++) {
      const z = startZ + (i - 0.5) * pitch;
      if (Math.abs(z) > length / 2 - barLen / 2) continue;
      const g = bevelBox(slotW, thickness, barLen, bev);
      g.translate(0, 0, z);
      parts.push(g);
    }
    const endLen = (length - (span + slotL)) / 2;
    if (endLen > 0.0008) {
      for (const sz of [-1, 1]) {
        const g = bevelBox(slotW, thickness, endLen, bev);
        g.translate(0, 0, sz * (length / 2 - endLen / 2));
        parts.push(g);
      }
    }
  } else {
    parts.push(bevelBox(slotW, thickness, length, bev));
  }
  return parts;
}

/** Merge a list of geometries that all carry position+normal(+uv). */
export function weld(geos) {
  const list = geos.filter(Boolean).map((g) => (g.index ? g.toNonIndexed() : g));
  const total = list.reduce((a, g) => a + g.attributes.position.count, 0);
  const pos = new Float32Array(total * 3);
  const nrm = new Float32Array(total * 3);
  let o = 0;
  for (const g of list) {
    const p = g.attributes.position.array;
    const n = g.attributes.normal ? g.attributes.normal.array : null;
    pos.set(p, o * 3);
    if (n) nrm.set(n, o * 3);
    o += g.attributes.position.count;
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
  return out;
}
